<template>
  <div class="insurance-preview">
    <div v-if="isPreview" class="preview-note">
      交互预览 · 使用演示数据，绑卡、签约及投保均不实际提交
    </div>
    <main
      class="phone-page"
      :class="{ 'applicant-page': page === 'applicant', 'insure-page': page === 'insurance' }"
    >
      <template v-if="page === 'applicant'">
        <section class="outlined applicant-card">
          <h2>申请人信息展示</h2>
          <dl>
            <dt>期望申请金额(元)</dt>
            <dd class="amount">{{ applicant.amount }}</dd>
            <dt>申请人姓名</dt>
            <dd class="insurance-private">{{ applicant.name }}</dd>
            <dt>申请人身份证号</dt>
            <dd class="insurance-private">{{ applicant.identity }}</dd>
            <dt>申请人手机号</dt>
            <dd class="insurance-private">{{ applicant.phone }}</dd>
            <dt>社保情况</dt>
            <dd>{{ applicant.socialSecurity }}</dd>
          </dl>
          <p v-if="detailLoading" class="muted">正在加载申请人信息…</p>
          <p v-if="detailError" class="error" role="alert">
            {{ detailError }} <button class="protocol-link" @click="loadApplicant">重试</button>
          </p>
        </section>
        <section class="outlined recommendation">
          <h2>推荐选购</h2>
          <p class="muted">当前推荐为保险类产品，由三方金融机构提供服务</p>
          <!-- 手机端使用压缩图片并按原比例展示，避免裁掉保障说明与保费信息。 -->
          <img
            class="insurance-banner applicant-banner"
            src="@/assets/images/insurance/recommendation-banner.jpg"
            alt="个人意外险推荐介绍"
            width="750"
            height="328"
          />
          <div class="agreement-row">
            <input
              id="insurance-contract"
              type="checkbox"
              :checked="insuranceAgreed"
              @change="toggleAgreement('insuranceAgreed', $event)"
            />
            <label for="insurance-contract">我已阅读并同意</label>
            <!-- 经纪公司五份协议共用本组手动勾选，阅读正文不自动代表同意。 -->
            <button
              v-for="name in recommendationAgreements"
              :key="name"
              class="protocol-link"
              @click="openProtocol(name)"
            >
              《{{ name }}》
            </button>
          </div>
          <div class="agreement-row">
            <input
              id="sharing-contract"
              type="checkbox"
              :checked="sharingAgreed"
              @change="toggleAgreement('sharingAgreed', $event)"
            />
            <label for="sharing-contract">我已阅读并同意</label>
            <button class="protocol-link" @click="openProtocol('个人信息共享授权协议')">
              《个人信息共享授权协议》
            </button>
          </div>
        </section>
        <footer class="applicant-footer">
          <button
            class="primary orange"
            :disabled="(!isPreview && !detailLoaded) || priceLoading"
            @click="next"
          >
            {{ priceLoading ? '正在获取报价…' : '确认并下一步' }}
          </button>
          <p class="muted">当前页面仅进行申请人信息展示，推荐选购由第三方机构提供服务</p>
        </footer>
      </template>

      <template v-else-if="page === 'insurance'">
        <section class="outlined insurance-detail">
          <img
            class="insurance-banner large"
            src="@/assets/images/insurance/confirm-banner.jpg"
            alt="个人意外险保障介绍"
            width="750"
            height="627"
          />
          <h2>投保信息确认</h2>
          <p v-if="detailLoading" class="muted">正在加载投保人信息…</p>
          <p v-if="detailError" class="error">
            {{ detailError }} <button class="protocol-link" @click="loadApplicant">重试</button>
          </p>
          <dl>
            <dt>申请人姓名</dt>
            <dd class="insurance-private">{{ applicant.name }}</dd>
            <dt>申请人身份证号</dt>
            <dd class="insurance-private">{{ applicant.identity }}</dd>
            <dt>申请人手机号</dt>
            <dd class="insurance-private">{{ insurancePhone }}</dd>
            <dt>社保情况</dt>
            <dd>{{ applicant.socialSecurity }}</dd>
          </dl>
          <div class="important-notice">
            <p>
              【重要提示】本保险产品与贷款业务相互独立，二者不存在绑定关系。投保本保险并非贷款申请、放款、续贷、降息的前提条件，不构成借款或业务办理的必要环节。
            </p>
            <p>
              保险保费单独计收，不计入贷款本金，也不会对贷款利率产生任何影响。请您充分知悉、理性谨慎投保。
            </p>
            <p>
              您现已进入保险投保流程，为维护您的合法权益，请您务必自主阅读保险条款、费率表、投保须知等全部相关文件。本次投保操作行为将被安全留存记录。
            </p>
            <p>
              本产品保费为{{
                insuranceMonthlyPrice()
              }}元/月，共计12期。保费标准与被保险人年龄、社保参保情况相关，具体计费规则请查询正式费率表；保险保费独立与贷款还款，二者扣款相互分离。
            </p>
            <p>
              <!-- 按产品指定分别展示个人信息提供对象与授权使用主体。 -->
              为向您提供便捷的保险服务，您在此授权可将您的个人信息（包括但不限于姓名、身份证号、手机号码、银行账户信息、第三方支付账号等）提供给上海汇中保险经纪有限责任公司，用于简化保险流程，免除您重复录入信息的操作。同时，您授权渤海财产保险股份有限公司接受、使用上述获取的个人信息，为您办理保险相关服务。
            </p>
          </div>
        </section>
        <footer class="insurance-footer">
          <div class="agreement-row">
            <input id="policy-contract" v-model="policyAgreed" type="checkbox" />
            <label for="policy-contract">我已阅读并同意</label>
            <button
              v-for="name in policyNames"
              :key="name"
              class="protocol-link orange-link"
              @click="openProtocol(name)"
            >
              《{{ name }}》
            </button>
          </div>
          <button
            class="primary orange"
            :disabled="insuring || (!isPreview && (!detailLoaded || detailLoading))"
            @click="confirmInsurance"
          >
            {{ insuring ? '正在提交…' : '确认投保' }}
          </button>
        </footer>
      </template>

      <template v-else>
        <div class="loan-result">
          <header>
            {{ conditions.productSuccess ? '恭喜您！贷款申请已成功' : '贷款申请结果' }}
          </header>
          <section class="result-card loan-card">
            <img
              class="phone-icon"
              src="../../assets/images/image_orange/result/top_phone.png"
              alt=""
            />
            <h2>{{ conditions.productSuccess ? '等待资方电话复审' : '本次申请暂未匹配成功' }}</h2>
            <p class="muted">
              {{
                conditions.productSuccess
                  ? '首次借款需资方客户经理电话联系您确认相关信息，请注意接听！'
                  : '您可以查看其他第三方金融产品，具体申请结果以机构审批为准。'
              }}
            </p>
            <div v-if="conditions.productSuccess" class="loan-info">
              <h3>测试产品（演示）</h3>
              <p>最高可借金额：<span>¥ 200000.00</span></p>
              <p>公司名称：测试机构</p>
              <p class="orange-text">*为保证电话复审通过，请你注意接听来电</p>
            </div>
          </section>
          <button class="primary orange" @click="showToast('预览页面不跳转贷款机构')">
            {{ conditions.productSuccess ? '确认额度' : '查看其他产品' }}
          </button>
          <!-- 失败结果不展示“风控已通过”，保险准入与产品1进件成败分别处理。 -->
          <section v-if="conditions.productSuccess" class="result-card">
            <h2>贷款流程</h2>
            <div class="loan-progress">
              <div>
                <img src="../../assets/images/image_orange/result/icon_safe.png" alt="" />
                <p>风控系统审核</p>
                <small>● 已通过</small>
              </div>
              <span>›</span>
              <div>
                <img src="../../assets/images/image_orange/result/icon_phone.png" alt="" />
                <p>电话复审</p>
                <small class="orange-text">● 进行中</small>
              </div>
              <span>›</span>
              <div>
                <img src="../../assets/images/image_orange/result/icon_money_none.png" alt="" />
                <p>申请提现</p>
                <small class="muted">● 等待电话复审</small>
              </div>
            </div>
          </section>
          <section v-if="insuranceSubmitted" class="result-card insurance-result">
            <h2>推荐选购结果</h2>
            <img
              class="insurance-banner result-banner"
              src="@/assets/images/insurance/result-banner.jpg"
              alt="个人意外险介绍"
              width="750"
              height="328"
            />
            <p>演示状态：保险申请已提交，尚未真实投保。</p>
            <p class="muted">保单状态、保障内容及服务信息以保险方实际返回为准。</p>
          </section>
          <section class="result-card">
            <h2>第三方金融产品</h2>
            <div class="other-product">
              <h3>演示推荐产品</h3>
              <p class="muted">可借额度范围（元）</p>
              <strong>¥ 10~100,000</strong
              ><button @click="showToast('预览页面不发起真实申请')">立即申请</button>
            </div>
          </section>
        </div>
      </template>

      <div
        v-if="authorizationVisible"
        class="mask"
        role="dialog"
        aria-modal="true"
        aria-label="温馨提示"
      >
        <section class="authorization-sheet">
          <!-- 图标与标题并排，压缩顶部留白，为授权正文保留更多阅读空间。 -->
          <header class="authorization-header">
            <div class="authorization-icon"><van-icon name="shield-o" /></div>
            <div>
              <h2>温馨提示</h2>
              <p>请阅读并确认以下授权内容</p>
            </div>
          </header>
          <div class="authorization-intro">
            <!-- 服务主体按产品确认的公司全称展示，授权正文仍沿用原内容来源。 -->
            <p>1. 您即将进入投保流程，保险服务由上海汇中保险经纪有限责任公司提供。</p>
            <p>2. 为保障你的合法权益，我们将会安全记录您的操作轨迹，请认真阅读以下授权协议。</p>
          </div>
          <article v-if="!isPreview" class="authorization-copy">
            <p v-if="authorizationLoading">正在加载个人信息授权书…</p>
            <template v-else-if="authorizationError"
              ><p class="error" role="alert">{{ authorizationError }}</p>
              <button class="protocol-link" @click="loadAuthorization">重新加载</button></template
            >
            <div v-else v-html="authorizationHtml" />
          </article>
          <article v-else class="authorization-copy">
            <h3>个人信息授权书</h3>
            <p>一、服务主体提示</p>
            <p>您即将进入第三方保险投保流程：</p>
            <p>1. 保险产品由【XX财产保险股份有限公司】承保；</p>
            <p>2. 贷款服务及第三方金融机构推荐服务由【闪借花】提供；</p>
            <p>3. 两者相互独立，是否投保不影响您的贷款审批结果。</p>
            <p>二、操作轨迹录制授权（自愿选择）</p>
            <p>
              为保障投保操作真实，便于风控核验与客诉回溯，本次投保流程将开启页面操作轨迹记录（DOM页面快照，非系统录屏）：
            </p>
            <p>1. 仅录制本投保页面内的操作，不录制系统桌面、其他APP、短信验证码；</p>
            <p>2. 轨迹数据由保险服务方接收，平台不持有、不存储操作轨迹数据；</p>
            <p>3. 数据加密传输、加密存储，仅用于投保核验与争议处理；</p>
            <p>4. 自投保小结之日起留存3年，到期自动删除；</p>
            <p>5. 您可向易诚保险经纪有限公司申请查询、删除相关数据，也可随时在投保页面关闭录制。</p>
            <p class="muted">
              预览说明：以上根据产品图展示，正式公司名称、完整授权文案及签署接口待补充。当前不产生实际签约或上传。
            </p>
          </article>
          <p v-if="error" class="error" role="alert">{{ error }}</p>
          <button
            class="primary orange authorization-button"
            :disabled="
              starting ||
              (!isPreview && (!detailLoaded || !authorizationHtml || authorizationLoading))
            "
            @click="agreeAuthorization"
          >
            {{ starting ? '正在准备…' : '我已阅读并同意授权' }}
          </button>
          <p v-if="!isPreview && detailError" class="error">
            {{ detailError }} <button class="protocol-link" @click="loadApplicant">重试</button>
          </p>
          <button class="decline-button" :disabled="starting" @click="declineAuthorization">
            不同意
          </button>
        </section>
      </div>

      <div v-if="cardVisible" class="mask" role="dialog" aria-modal="true" aria-label="绑定银行卡">
        <section class="bank-sheet">
          <button
            class="close-button"
            aria-label="关闭绑卡弹窗"
            :disabled="binding"
            @click="cardVisible = false"
          >
            ×
          </button>
          <h2>绑定银行卡</h2>
          <p class="bank-subtitle">
            请输入您的银行卡信息，<span class="orange-text">仅支持储蓄卡</span>
          </p>
          <div class="bank-fields insurance-private">
            <div class="bank-row">
              <span>持卡人</span><span>{{ applicant.name }}</span>
            </div>
            <label class="bank-row"
              ><span>银行卡号</span
              ><input
                v-model.trim="bank.number"
                inputmode="numeric"
                @blur="bank.number && validateBankNumber()"
                maxlength="21"
                placeholder="请输入储蓄卡/借记卡"
                autocomplete="off"
            /></label>
            <p v-if="bankNumberError" class="bank-field-error" role="alert">
              {{ bankNumberError }}
            </p>
            <button
              class="bank-row bank-select"
              type="button"
              aria-label="选择所属银行"
              @click="openBankPicker"
            >
              <span>所属银行</span>
              <span class="bank-selection" :class="{ placeholder: !bank.name }">
                {{ bankListLoading ? '正在获取银行…' : bank.name || '请选择所属银行' }}
                <van-icon name="arrow" />
              </span>
            </button>
            <div class="bank-row">
              <label for="bank-phone">银行预留手机号</label>
              <input
                id="bank-phone"
                v-model.trim="bank.phone"
                inputmode="tel"
                @input="onBankPhoneInput"
                @blur="validateBankPhone"
                maxlength="11"
                placeholder="请输入银行预留手机号"
                autocomplete="off"
              />
              <button
                v-if="bank.phone"
                class="bank-phone-clear"
                type="button"
                aria-label="清除银行预留手机号"
                @click="clearBankPhone"
              >
                <van-icon name="clear" />
              </button>
            </div>
            <p v-if="bankPhoneError" class="bank-field-error" role="alert">{{ bankPhoneError }}</p>
          </div>
          <p class="bank-warning">
            当前页面填写的手机号，需与银行预留的手机号保持一致，否则会导致绑卡失败。如绑卡失败，请绑定新的银行卡
          </p>
          <div class="bank-bottom">
            <div class="agreement-row">
              <input id="bank-contract" v-model="bankAgreed" type="checkbox" /><label
                for="bank-contract"
                >我已阅读并同意</label
              ><button
                class="protocol-link orange-link"
                @click="openProtocol('委托代扣授权书及告知书')"
              >
                《委托代扣授权书及告知书》
              </button>
            </div>
            <button class="primary orange" :disabled="binding" @click="bindCard">
              {{ binding ? '正在申请…' : '我已阅读并同意授权' }}
            </button>
            <p class="muted">当前页面由推荐选购的第三方机构提供服务</p>
          </div>
        </section>
        <!-- 银行面板仅展示可选银行，不展示接口接入状态等开发提示。 -->
        <van-action-sheet
          v-model="bankPickerVisible"
          :actions="bankOptions"
          title="选择所属银行"
          cancel-text="取消"
          get-container="body"
          :z-index="2200"
          @select="selectBank"
        />
      </div>

      <!-- 已签约不再发起短信验证；提示复用原签约信息后，由按钮或倒计时进入预投保页。 -->
      <div
        v-if="signedNoticeVisible"
        class="mask signed-notice-mask"
        role="dialog"
        aria-modal="true"
        aria-label="已签约提示"
      >
        <section class="signed-notice-sheet">
          <h2>检测到您之前完成过签约，<br />系统将使用您之前的签约、绑卡信息</h2>
          <p class="signed-notice-description">
            如您不知道之前的签约信息，请进入预投保页面，根据页面信息联系相应的承保机构，查询签约信息
          </p>
          <button class="primary orange" @click="enterPreInsurance">
            进入预投保页面({{ signedNoticeCountdown }}S)
          </button>
          <p class="signed-notice-disclaimer">
            保险服务由第三方保险经纪公司和第三方保险公司提供服务，本平台不参与该第三方机构的任何业务环节。
          </p>
        </section>
      </div>

      <div
        v-if="smsVisible"
        class="mask sms-mask"
        role="dialog"
        aria-modal="true"
        aria-label="请填写验证码"
      >
        <section class="sms-sheet">
          <button
            class="close-button"
            aria-label="关闭短信验证弹窗"
            :disabled="smsConfirming || smsSending"
            @click="closeSms"
          >
            ×
          </button>
          <h2>请填写验证码</h2>
          <div class="sms-input-row insurance-private">
            <input
              v-model.trim="smsCode"
              aria-label="短信验证码"
              inputmode="numeric"
              maxlength="6"
              autocomplete="one-time-code"
              placeholder="请输入6位验证码"
            />
            <button :disabled="smsCountdown > 0 || smsSending || smsConfirming" @click="resendSms">
              {{ smsCountdown > 0 ? smsCountdown + 's' : smsSending ? '发送中…' : '重新获取' }}
            </button>
          </div>
          <button
            class="primary orange"
            :disabled="smsConfirming || smsSending"
            @click="confirmSms"
          >
            {{ smsConfirming ? '正在验证…' : '提交验证码' }}
          </button>
        </section>
      </div>

      <div
        v-if="protocol"
        class="mask protocol-mask"
        role="dialog"
        aria-modal="true"
        :aria-label="protocol"
      >
        <section class="protocol-sheet">
          <h2>{{ protocol }}</h2>
          <div
            v-if="localAgreementNames.includes(protocol)"
            :key="protocol"
            class="remote-protocol local-protocol"
            v-html="protocolHtml"
          />
          <template v-else-if="protocol === '个人信息共享授权协议' && !isPreview">
            <p v-if="protocolLoading">正在加载协议…</p>
            <p v-else-if="protocolError" class="error" role="alert">
              {{ protocolError }}
              <button class="protocol-link" @click="openProtocol(protocol)">重试</button>
            </p>
            <div v-else class="remote-protocol" v-html="protocolHtml" />
          </template>
          <p v-else>
            {{
              protocol === '支持银行'
                ? '真实支持银行列表由绑卡服务方提供，当前仅演示银行选择交互。'
                : '协议正文暂未提供，后续补充后将在此展示。'
            }}
          </p>
          <button class="primary orange" @click="protocol = ''">关闭</button>
        </section>
      </div>
    </main>
    <details v-if="isPreview" class="preview-controls">
      <summary>预览条件与录制状态</summary>
      <p>以下设置仅用于独立预览，不控制正式进件接口。</p>
      <label
        ><input v-model="conditions.productSuccess" type="checkbox" />
        产品1进件成功（取消为失败）</label
      >
      <label><input v-model="conditions.hasSocialSecurity" type="checkbox" /> 有社保</label>
      <label
        ><input v-model="conditions.hasInsuranceProduct" type="checkbox" /> 有可申请保险产品</label
      >
      <label
        ><input v-model="previewOnly" type="checkbox" :disabled="page !== 'result' || recording" />
        仅演示授权交互，不调用录制脚本（重新开始前可切换）</label
      >
      <p>
        录制状态：{{ recording ? '实际记录中，仅保存本地内存' : '未录制 / 已停止' }} ·
        {{ eventCount }} 条事件
      </p>
      <button class="primary orange" @click="restart">按上述条件重新预览</button>
    </details>
  </div>
</template>

<script>
import { createInsuranceRecorder, handoffResultRecording } from '@/utils/insuranceRecorder'
import * as API_Common from '@/api/common.js'
import * as InsuranceAPI from '@/api/insurance.js'
import insuranceAgreements from '@/assets/insurance/agreements.json'
import insuranceProductTerms from '@/assets/insurance/product-terms.json'

// 两组协议共用阅读弹窗，产品条款与经纪公司协议仍按各自页面展示。
const localAgreements = { ...insuranceAgreements, ...insuranceProductTerms }

export default {
  name: 'InsuranceFlowPreview',
  props: {
    isPreview: { type: Boolean, default: true },
    source: { type: String, default: '' },
    insuranceProductCode: { type: String, default: '' },
  },
  data() {
    return {
      page: 'applicant',
      conditions: { productSuccess: true, hasSocialSecurity: true, hasInsuranceProduct: true },
      applicant: this.isPreview
        ? {
            amount: '200,000',
            name: '王**耀',
            identity: '220281********7810',
            phone: '152****7810',
            socialSecurity: '有社保',
          }
        : { amount: '—', name: '—', identity: '—', phone: '—', socialSecurity: '—' },
      detailLoading: false,
      detailLoaded: false,
      detailError: '',
      authorizationHtml: '',
      authorizationLoading: false,
      authorizationError: '',
      protocolHtml: '',
      // 推荐区与绑卡区分别展示指定协议，正文取用户提供的文档，不替换906/907接口协议。
      recommendationAgreements: [
        '用户协议',
        '隐私政策',
        '授权协议',
        '客户告知书',
        '自动重新投保服务协议',
      ],
      localAgreementNames: Object.keys(localAgreements),
      protocolLoading: false,
      protocolError: '',
      insuranceAgreed: false,
      sharingAgreed: false,
      policyAgreed: false,
      bankAgreed: false,
      authorizationVisible: true,
      authorizationAccepted: false,
      cardVisible: false,
      bankPickerVisible: false,
      bankOptions: this.isPreview ? [{ name: '演示银行（仅预览）', value: '演示银行' }] : [],
      bank: { number: '', name: '', phone: this.isPreview ? '15200007810' : '', code: '' },
      bankPhoneEdited: false,
      priceLoading: false,
      priceList: [],
      bankListLoading: false,
      bankNumberError: '',
      bankPhoneError: '',
      selectedBank: null,
      binding: false,
      bindResult: null,
      insurancePhone: '—',
      signedNoticeVisible: false,
      signedNoticeCountdown: 0,
      smsVisible: false,
      smsCode: '',
      smsCountdown: 0,
      smsSending: false,
      smsConfirming: false,
      policyNames: [
        '投保须知',
        '保障详情及费率',
        '人身意外伤害保险条款',
        '附加人身意外伤害医疗保险条款',
        '特别约定',
        '责任免除',
      ],
      insuranceSubmitted: false,
      insuring: false,
      insuranceResult: null,
      protocol: '',
      previewOnly: this.isPreview,
      starting: false,
      recording: false,
      eventCount: 0,
      error: '',
    }
  },
  created() {
    // 录制快照不进入响应式数据，避免 Vue 对大量 DOM 事件进行递归观察。
    this.recorder = null
    // 请求报价与银行列表需使用完整身份信息，单独保留，不参与信息展示与录屏快照。
    this.requestIdentity = null
    this.bindSession = null
    this.smsTimer = null
    this.signedNoticeTimer = null
    this.disposed = false
    if (!this.isPreview) {
      this.loadApplicant()
      this.loadAuthorization()
    }
  },
  beforeDestroy() {
    this.disposed = true
    this.closeSms()
    this.clearSignedNotice()
    this.requestIdentity = null
    this.stopRecording()
    this.recorder = null
  },
  methods: {
    showToast(message) {
      this.$toast(message)
    },
    async loadApplicant() {
      this.detailLoading = true
      this.detailError = ''
      try {
        const res = await API_Common.getUserDetailOld(this.source)
        if (!res || res.code !== 200 || !res.data) throw new Error('申请人信息加载失败')
        if (this.disposed) return
        this.setApplicant(res.data)
        this.detailLoaded = true
      } catch (error) {
        if (!this.disposed) this.detailError = '申请人信息加载失败，请重试'
      } finally {
        if (!this.disposed) this.detailLoading = false
      }
    },
    setApplicant(data) {
      // 与进件表单使用同一详情字段，仅将掩码后的身份信息放入页面展示数据。
      const name = String(data.userName || '')
      const identity = String(data.idCard || '')
      const phone = String(data.mobile || '')
      this.requestIdentity = { idCard: identity, name, phoneNo: phone }
      // 用户清除或改写后保留输入，不用详情回显覆盖；掩码回显不视为手工输入。
      if (!this.bankPhoneEdited && !this.bank.phone) this.bank.phone = phone
      const social =
        data.socialSecurity != null
          ? data.socialSecurity
          : Array.isArray(data.assets)
          ? data.assets.includes(4)
          : null
      this.applicant = {
        amount:
          data.loanAmount != null && data.loanAmount !== ''
            ? Number(data.loanAmount).toLocaleString('en-US')
            : '—',
        name: !name
          ? '—'
          : name.includes('*')
          ? name
          : name.length === 1
          ? '*'
          : name[0] + '*'.repeat(name.length - 1),
        identity: !identity
          ? '—'
          : identity.includes('*')
          ? identity
          : identity.slice(0, 6) +
            '*'.repeat(Math.max(0, identity.length - 10)) +
            identity.slice(-4),
        phone: !phone ? '—' : phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2'),
        socialSecurity:
          social == null ? '—' : [true, 1, '1'].includes(social) ? '有社保' : '无社保',
      }
    },
    async loadAuthorization() {
      this.authorizationLoading = true
      this.authorizationError = ''
      this.authorizationHtml = ''
      try {
        // 906 为操作记录授权书；正文加载成功前禁止确认授权，不回退到演示文案。
        const res = await API_Common.getContent({ pageType: 906, source: this.source })
        if (!res || res.code !== 200 || !res.data || !res.data.content)
          throw new Error('授权书加载失败')
        if (!this.disposed) this.authorizationHtml = res.data.content
      } catch (error) {
        if (!this.disposed) this.authorizationError = '个人信息授权书加载失败，请重试'
      } finally {
        if (!this.disposed) this.authorizationLoading = false
      }
    },
    async openProtocol(name) {
      this.protocol = name
      this.protocolHtml = ''
      this.protocolError = ''
      this.protocolLoading = false
      // 本地协议使用文档原文，阅读或关闭弹窗均不改变用户手动勾选状态。
      if (localAgreements[name]) {
        // PDF逐页图片保留原版表格，资源地址随部署目录变化，避免非根目录部署时图片失效。
        this.protocolHtml = localAgreements[name]
          .split('__INSURANCE_BASE__')
          .join(process.env.BASE_URL)
        return
      }
      if (this.isPreview || name !== '个人信息共享授权协议') return
      this.protocolLoading = true
      try {
        // 907 为个人信息共享授权协议，沿用项目协议接口的 content 字段渲染。
        const res = await API_Common.getContent({ pageType: 907, source: this.source })
        if (!res || res.code !== 200 || !res.data || !res.data.content)
          throw new Error('协议加载失败')
        if (!this.disposed && this.protocol === name) this.protocolHtml = res.data.content
      } catch (error) {
        if (!this.disposed && this.protocol === name) this.protocolError = '协议加载失败，请重试'
      } finally {
        if (!this.disposed && this.protocol === name) this.protocolLoading = false
      }
    },
    toggleAgreement(key, event) {
      const checked = event.target.checked
      if (!checked) {
        this[key] = false
        return
      }
      // 勾选两组协议时分别检查授权；拒绝只关闭弹窗，不代替用户选中协议。
      if (!this.authorizationAccepted || (!this.previewOnly && !this.recording)) {
        // 原生勾选先于 change 事件生效，等待授权时须立即恢复，避免拒绝后界面仍显示选中。
        event.target.checked = false
        this.authorizationVisible = true
        this.error = ''
        return
      }
      this[key] = true
    },
    async agreeAuthorization() {
      if (this.starting) return
      if (
        !this.isPreview &&
        (!this.detailLoaded || !this.authorizationHtml || this.authorizationLoading)
      )
        return
      this.starting = true
      this.error = ''
      try {
        // 独立预览可演示签署交互；只有关闭演示模式后才调用真实 SDK，绝不伪造录制状态。
        if ((!this.isPreview || !this.previewOnly) && !this.recording) {
          const recorder = await createInsuranceRecorder((sessionId, events) => {
            this.recording = false
            this.eventCount = events.length
            if (!this.disposed && !this.stoppingRecording && this.page !== 'result')
              this.showToast('操作记录已停止，请重新确认授权后继续')
          })
          if (this.disposed) return
          this.recorder = recorder
          if (!recorder.start() || !recorder.stopFn) throw new Error('录制启动失败，请重试')
          this.recording = recorder.isRecording
        }
        // 授权仅确认操作记录权限；推荐选购协议必须在弹窗关闭后由用户重新手动勾选。
        this.authorizationAccepted = true
        this.authorizationVisible = false
      } catch (error) {
        this.stopRecording()
        this.error = error.message || '录制启动失败，请重试'
      } finally {
        this.starting = false
      }
    },
    declineAuthorization() {
      // 产品文档要求“不同意”仅关闭蒙层，仍停留申请人信息页。
      this.authorizationVisible = false
      this.error = ''
    },
    async next() {
      if (this.priceLoading) return
      if (!this.isPreview && !this.detailLoaded) return
      const selected = Number(this.insuranceAgreed) + Number(this.sharingAgreed)
      if (selected === 0) {
        this.showResult(false)
      } else if (selected === 1) {
        this.showToast('您已勾选推荐选购服务，请确认同意全部协议，或取消全部协议')
      } else if (!this.authorizationAccepted || (!this.previewOnly && !this.recording)) {
        this.authorizationVisible = true
      } else {
        if (this.isPreview) {
          this.cardVisible = true
          return
        }
        if (
          !this.insuranceProductCode ||
          !this.requestIdentity ||
          !this.requestIdentity.idCard ||
          this.requestIdentity.idCard.includes('*')
        ) {
          return this.showToast('缺少保险产品编码或完整身份证信息，暂无法获取报价')
        }
        this.priceLoading = true
        this.priceList = []
        try {
          // 此接口按协议勾选状态传1/0，不使用用户详情中的社保字段；未勾选两组协议时不请求报价。
          const res = await InsuranceAPI.getInsurancePrices({
            productCode: this.insuranceProductCode,
            idCard: this.requestIdentity.idCard,
            hasSocialSecurity: Number(this.insuranceAgreed && this.sharingAgreed),
          })
          if (this.disposed) return
          if (!res || res.code !== 200 || !Array.isArray(res.data) || !res.data.length) {
            throw new Error((res && res.msg) || '暂无可用保险报价，请稍后重试')
          }
          // 保留全部报价及版本字段，后续投保使用；当前不擅自选择基础版或升级版。
          this.priceList = res.data
          // 等待报价期间用户若撤销协议，不再继续打开绑卡弹窗。
          if (!this.insuranceAgreed || !this.sharingAgreed) return
          this.cardVisible = true
        } catch (error) {
          if (!this.disposed) this.showToast(error.message || '获取保险报价失败，请重试')
        } finally {
          if (!this.disposed) this.priceLoading = false
        }
      }
    },
    async openBankPicker() {
      if (this.bankListLoading) return
      if (this.isPreview) {
        this.bankPickerVisible = true
        return
      }
      if (!this.requestIdentity) return this.showToast('申请人信息未加载，请稍后重试')
      // 银行选择不依赖手机号填写顺序；手机号必填及格式校验仅在提交绑卡时执行。
      this.bankListLoading = true
      try {
        const res = await InsuranceAPI.getInsuranceBanks({
          productCode: this.insuranceProductCode,
          idCard: this.requestIdentity.idCard,
          name: this.requestIdentity.name,
          phoneNo: this.bank.phone,
        })
        if (this.disposed) return
        if (!res || res.code !== 200 || !Array.isArray(res.data) || !res.data.length) {
          throw new Error((res && res.msg) || '暂无支持银行，请稍后重试')
        }
        // 同时保留银行编码、支付渠道及卡类型字段，供后续真实绑卡使用。
        this.bankOptions = res.data.map((item) => ({
          ...item,
          name: item.bankName,
          value: item.bankCode,
        }))
        this.bankPickerVisible = true
      } catch (error) {
        if (!this.disposed) this.showToast(error.message || '获取银行列表失败，请重试')
      } finally {
        if (!this.disposed) this.bankListLoading = false
      }
    },
    selectBank(option) {
      // 银行选择沿用项目已有的移动端面板，选中后回填并收起，不直接发起绑卡。
      this.bank.name = option.bankName || option.name
      this.bank.code = option.bankCode || option.value
      this.selectedBank = option
      this.bankPickerVisible = false
    },
    validateBankNumber() {
      // 接口规定银行卡号为16至21位纯数字（^\d{16,21}$）；仅校验格式，不代替银行真实性校验。
      this.bankNumberError = /^\d{16,21}$/.test(this.bank.number)
        ? ''
        : '请输入16至21位纯数字银行卡号'
      return !this.bankNumberError
    },
    onBankPhoneInput() {
      // 仅真实输入或主动清除切换校验模式，程序回显（可能含掩码）无需通过手机号正则。
      this.bankPhoneEdited = true
      this.bankPhoneError = ''
    },
    clearBankPhone() {
      this.bank.phone = ''
      this.onBankPhoneInput()
    },
    validateBankPhone() {
      // 非空回显跳过正则；用户修改后须以1开头且为11位数字（^1\d{10}$），空值仍必填。
      this.bankPhoneError = !this.bank.phone
        ? '请输入银行预留手机号'
        : this.bankPhoneEdited && !/^1\d{10}$/.test(this.bank.phone)
        ? '请输入正确的银行预留手机号'
        : ''
      return !this.bankPhoneError
    },
    async bindCard() {
      if (this.binding || this.signedNoticeVisible) return
      if (!this.validateBankNumber()) return
      if (!this.bank.name) return this.showToast('请选择所属银行')
      if (!this.validateBankPhone()) return
      if (!this.bankAgreed) return this.showToast('请阅读并同意绑卡协议')
      if (
        !this.isPreview &&
        (!this.requestIdentity ||
          !this.insuranceProductCode ||
          !this.selectedBank ||
          !this.selectedBank.bankCode ||
          !this.selectedBank.payChannelId)
      ) {
        return this.showToast('缺少绑卡信息，请重新选择银行后重试')
      }
      this.binding = true
      try {
        const request = this.isPreview
          ? {}
          : {
              bankCard: this.bank.number,
              bankCode: this.selectedBank.bankCode,
              userName: this.requestIdentity.name,
              idCard: this.requestIdentity.idCard,
              phoneNo: this.bank.phone,
              productCode: this.insuranceProductCode,
              payChannelId: this.selectedBank.payChannelId,
              cardType: '1',
            }
        const res = this.isPreview
          ? {
              code: 200,
              data: {
                signStatus: '0',
                sessionId: 'preview',
                outTradeNo: 'preview',
                bindPhone: this.bank.phone,
              },
            }
          : await InsuranceAPI.applyBindCard(request)
        if (this.disposed) return
        if (!res || res.code !== 200 || !res.data)
          throw new Error((res && (res.message || res.msg)) || '绑卡申请失败，请重试')
        const status = String(res.data.signStatus)
        // 投保页展示本次申请返回的签约手机号，独立保存脱敏值，避免短信确认或详情刷新覆盖。
        this.insurancePhone =
          String(res.data.bindPhone || '').replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') || '—'
        if (status === '1') {
          this.openSignedNotice(res.data)
        } else if (status === '0') {
          if (!res.data.sessionId || !res.data.outTradeNo)
            throw new Error('绑卡短信会话缺失，请重新申请')
          // 保存申请时的上下文，短信接口不混用后续编辑的银行卡或银行渠道。
          this.bindSession = {
            sessionId: res.data.sessionId,
            outTradeNo: res.data.outTradeNo,
            payChannelId: request.payChannelId,
            productCode: request.productCode,
          }
          this.smsCode = ''
          this.smsVisible = true
          this.startSmsCountdown()
          this.showToast('验证码已发送')
        } else throw new Error(res.message || res.msg || '绑卡失败，请重试')
      } catch (error) {
        if (!this.disposed) this.showToast(error.message || '绑卡失败，请重试')
      } finally {
        if (!this.disposed) this.binding = false
      }
    },
    startSmsCountdown() {
      clearInterval(this.smsTimer)
      this.smsCountdown = 60
      // 使用截止时间计算剩余秒数，切到后台再返回时不会延长重发等待时间。
      const deadline = Date.now() + 60000
      this.smsTimer = setInterval(() => {
        this.smsCountdown = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
        if (!this.smsCountdown) {
          clearInterval(this.smsTimer)
          this.smsTimer = null
        }
      }, 1000)
    },
    closeSms() {
      clearInterval(this.smsTimer)
      this.smsTimer = null
      this.smsVisible = false
      this.smsCountdown = 0
      this.smsCode = ''
      this.bindSession = null
    },
    async resendSms() {
      if (this.smsCountdown || this.smsSending || this.smsConfirming || !this.bindSession) return
      this.smsSending = true
      try {
        const res = this.isPreview
          ? { code: 200 }
          : await InsuranceAPI.resendBindCardSms({ ...this.bindSession })
        if (this.disposed || !this.smsVisible) return
        if (!res || res.code !== 200)
          throw new Error((res && (res.message || res.msg)) || '验证码发送失败，请重试')
        this.startSmsCountdown()
        this.showToast('验证码已发送')
      } catch (error) {
        if (!this.disposed) this.showToast(error.message || '验证码发送失败，请重试')
      } finally {
        if (!this.disposed) this.smsSending = false
      }
    },
    async confirmSms() {
      if (this.smsConfirming || this.smsSending || !this.bindSession) return
      // 绑卡短信统一为6位纯数字，长度或格式不符时不提交确认接口。
      if (!/^\d{6}$/.test(this.smsCode)) return this.showToast('请输入6位数字验证码')
      this.smsConfirming = true
      try {
        const res = this.isPreview
          ? { code: 200, data: { signStatus: '1' } }
          : await InsuranceAPI.confirmBindCard({ ...this.bindSession, smsCode: this.smsCode })
        if (this.disposed || !this.smsVisible) return
        if (!res || res.code !== 200 || !res.data || String(res.data.signStatus) !== '1')
          throw new Error((res && (res.message || res.msg)) || '绑卡失败，请重试')
        this.finishBinding(res.data)
      } catch (error) {
        if (!this.disposed) this.showToast(error.message || '绑卡失败，请重试')
      } finally {
        if (!this.disposed) this.smsConfirming = false
      }
    },
    openSignedNotice(data) {
      this.clearSignedNotice()
      this.bindResult = data
      this.signedNoticeVisible = true
      this.signedNoticeCountdown = 3
      // 使用截止时间，后台恢复后不会额外延长提示；点击与计时结束共用防重入口。
      const deadline = Date.now() + 3000
      this.signedNoticeTimer = setInterval(() => {
        if (this.disposed) return this.clearSignedNotice()
        this.signedNoticeCountdown = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
        if (!this.signedNoticeCountdown) this.enterPreInsurance()
      }, 1000)
    },
    clearSignedNotice() {
      clearInterval(this.signedNoticeTimer)
      this.signedNoticeTimer = null
      this.signedNoticeVisible = false
      this.signedNoticeCountdown = 0
    },
    enterPreInsurance() {
      if (!this.signedNoticeVisible || this.disposed) return
      this.finishBinding(this.bindResult)
    },
    finishBinding(data) {
      this.clearSignedNotice()
      this.bindResult = data
      // 仅接口明确返回已绑定才进入投保页；短信失败保留绑卡表单与当前会话供重试。
      this.closeSms()
      this.cardVisible = false
      this.page = 'insurance'
      this.policyAgreed = false
      // 投保页重新获取最新留资信息；不允许沿用加载失败前的数据直接提交。
      if (!this.isPreview) {
        this.detailLoaded = false
        this.loadApplicant()
      }
      window.scrollTo(0, 0)
    },
    insuranceMonthlyPrice() {
      // 与确认投保默认提交的升级版 productType=2 对齐，展示报价的 price，而非年保费或其他版本价格。
      const quote = this.priceList.find((item) => String(item.productType) === '2')
      return quote &&
        quote.price != null &&
        quote.price !== '' &&
        Number.isFinite(Number(quote.price))
        ? quote.price
        : '—'
    },
    async confirmInsurance() {
      if (this.insuring) return
      if (!this.policyAgreed) return this.showToast('如已阅读并同意协议内容，请勾选协议')
      if (this.isPreview) {
        this.showResult(true)
        return
      }
      if (
        !this.detailLoaded ||
        this.detailLoading ||
        !this.requestIdentity ||
        !this.insuranceProductCode
      )
        return this.showToast('请等待投保信息加载完成后重试')
      this.insuring = true
      try {
        const identity = this.requestIdentity
        // 可选参数按接口文档默认值提交；录制会话使用 SDK 的 sessionId，不额外发起上传。
        const res = await InsuranceAPI.confirmInsure({
          productCode: this.insuranceProductCode,
          phoneNo: identity.phoneNo,
          name: identity.name,
          idCard: identity.idCard,
          screenRecordId: this.recorder ? this.recorder.sessionId || '' : '',
          productType: '2',
          isUpgrade: 1,
          autoRenew: 1,
        })
        if (this.disposed) return
        if (!res || res.code !== 200 || !res.data)
          throw new Error((res && (res.message || res.msg)) || '投保申请失败，请重试')
        this.insuranceResult = res.data
        this.insuranceSubmitted = true
        // 将录制实例交给结果页，销毁当前弹层时不提前停止。
        handoffResultRecording(this.recorder)
        this.recorder = null
        // 直接交回进件页进入原结果模板，不再增加独立保险结果页或二次继续按钮。
        this.$emit('insured', res.data)
      } catch (error) {
        if (!this.disposed) this.showToast(error.message || '投保申请失败，请重试')
      } finally {
        if (!this.disposed) this.insuring = false
      }
    },
    insuranceStatusText(status) {
      // 后端成功返回只代表请求受理，状态9仍展示失败，未映射状态不猜测保单已生效。
      const labels = {
        0: '撞库成功',
        1: '绑卡中',
        2: '已绑卡',
        3: '预投保',
        4: '已发起扣款',
        5: '扣款成功',
        6: '退款成功',
        9: '失败',
      }
      return labels[status] || '处理中'
    },
    showResult(hasInsurance) {
      if (!this.isPreview) {
        this.stopRecording()
        this.$emit('continue')
        return
      }
      // 投保完成后回到贷款结果展示，在原贷款结果结构中按需插入推荐选购结果。
      this.insuranceSubmitted = hasInsurance
      this.page = 'result'
      this.cardVisible = false
      this.stopRecording()
      window.scrollTo(0, 0)
    },
    stopRecording() {
      // 主动离开流程时正常收尾；只有 SDK 自行停录才提示重新确认授权。
      this.stoppingRecording = true
      try {
        if (this.recorder) {
          if (this.recorder.isRecording) this.recorder.stop()
          this.eventCount = this.recorder.events.length
        }
        this.recording = false
      } finally {
        this.stoppingRecording = false
      }
    },
    restart() {
      this.closeSms()
      this.clearSignedNotice()
      this.stopRecording()
      this.recorder = null
      this.authorizationAccepted = false
      this.insuranceAgreed = false
      this.sharingAgreed = false
      this.policyAgreed = false
      this.bankAgreed = false
      this.insuranceSubmitted = false
      this.insuranceResult = null
      this.insurancePhone = '—'
      this.cardVisible = false
      this.bankPickerVisible = false
      this.protocol = ''
      this.bank = { number: '', name: '', phone: this.isPreview ? '15200007810' : '', code: '' }
      this.bankPhoneEdited = false
      this.bankNumberError = ''
      this.bankPhoneError = ''
      this.selectedBank = null
      this.priceList = []
      this.eventCount = 0
      this.error = ''
      // 准入条件仅演示产品图的社保及可申请产品两层判断，正式资格由进件接口决定。
      const eligible = this.conditions.hasSocialSecurity && this.conditions.hasInsuranceProduct
      this.page = eligible ? 'applicant' : 'result'
      this.authorizationVisible = eligible
      window.scrollTo(0, 0)
    },
  },
}
</script>

<style scoped lang="scss">
// 还原产品图的移动端页面、底部授权及绑卡弹窗，不在业务页面展示开发步骤条。
.insurance-preview {
  min-height: 100vh;
  background: #f3f4f6;
  color: #333;
  font-size: 13px;
  line-height: 1.5;
  .preview-note {
    padding: 7px 10px;
    background: #fff8ed;
    color: #9a6d32;
    text-align: center;
    font-size: 11px;
  }
  .phone-page {
    max-width: 390px;
    min-height: 660px;
    margin: 0 auto;
    padding: 12px 10px;
    background: #fff;
    position: relative;
  }
  h2 {
    font-size: 15px;
    font-weight: bold;
    line-height: 1.5;
  }
  h3 {
    font-size: 13px;
    font-weight: normal;
  }
  .outlined {
    border: 1px solid #aaa;
    border-radius: 6px;
    padding: 10px;
  }
  .applicant-card {
    min-height: 208px;
  }
  dl {
    display: grid;
    grid-template-columns: 130px 1fr;
    align-items: center;
    gap: 12px 3px;
    margin-top: 12px;
    font-size: 12px;
  }
  dd {
    text-align: right;
    overflow-wrap: anywhere;
  }
  .amount {
    font-size: 20px;
  }
  .recommendation {
    margin-top: 12px;
  }
  .muted {
    color: #999;
    font-size: 10px;
  }
  .insurance-banner {
    // 不固定高度，横幅与投保页大图均保留完整内容和自身宽高比。
    display: block;
    width: 100%;
    height: auto;
    border-radius: 12px;
    margin: 3px 0 10px;
  }
  .agreement-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    margin: 9px 0;
    font-size: 11px;
    line-height: 1.6;
  }
  input[type='checkbox'] {
    // 自绘白色对勾，避免系统原生控件在橙色背景上显示深色勾选标记。
    -webkit-appearance: none;
    appearance: none;
    width: 15px;
    height: 15px;
    margin: 0 2px 0 0;
    border: 1px solid #cbd0d7;
    border-radius: 4px;
    background: #fff;
    flex-shrink: 0;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  input[type='checkbox']:checked {
    border-color: #ff6c30;
    background: #ff6c30
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='m4 8 2.5 2.5L12 5' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
      center / 100% 100% no-repeat;
  }
  input[type='checkbox']:focus-visible {
    outline: 2px solid #ffc5a8;
    outline-offset: 2px;
  }
  input[type='checkbox']:disabled {
    opacity: 0.5;
    cursor: default;
  }
  button {
    cursor: pointer;
    font: inherit;
  }
  .protocol-link {
    color: #fd6f34;
    background: transparent;
    font-size: 11px;
  }
  .orange-link,
  .orange-text {
    color: #ff773f;
  }
  .primary {
    display: block;
    width: 100%;
    min-height: 39px;
    border-radius: 24px;
    color: white;
    font-size: 16px;
    padding: 7px 12px;
  }
  .primary:disabled {
    opacity: 0.55;
    cursor: default;
  }
  .orange {
    background: #ff773f;
  }
  // 底部操作区固定在可视区域，正文同步预留高度，长协议及安全区不会被按钮遮挡。
  .applicant-page {
    min-height: 100vh;
    padding: 20px 16px calc(142px + env(safe-area-inset-bottom));
    background: linear-gradient(180deg, #fff2e9 0, #f7f8fa 240px);
    .outlined {
      background: #fff;
      border: 1px solid #f0eeec;
      border-radius: 16px;
      padding: 18px 16px;
      box-shadow: 0 4px 18px rgba(102, 67, 44, 0.035);
    }
    > .outlined > h2 {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      color: #30343b;
      &::before {
        content: '';
        width: 3px;
        height: 14px;
        border-radius: 2px;
        background: #fd6f34;
      }
    }
    dl {
      grid-template-columns: 1fr auto;
      gap: 15px 10px;
      margin-top: 18px;
      font-size: 13px;
    }
    dt {
      color: #8b8f97;
    }
    dd {
      color: #414751;
    }
    dt:first-of-type,
    .amount {
      padding-bottom: 15px;
      border-bottom: 1px solid #f3f3f3;
    }
    .amount {
      font-size: 27px;
      line-height: 1.3;
      font-weight: 600;
      color: #f46a30;
    }
    .recommendation {
      margin-top: 16px;
    }
    .recommendation > .muted {
      margin: 7px 0 14px;
      font-size: 11px;
    }
    .agreement-row {
      gap: 3px;
      margin-top: 13px;
      line-height: 1.9;
      color: #757b85;
    }
    .protocol-link {
      color: #eb713b;
    }
  }
  .applicant-banner {
    margin: 0 0 18px;
  }
  .applicant-footer {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 390px;
    z-index: 20;
    padding: 14px 18px calc(12px + env(safe-area-inset-bottom));
    background: #fff;
    border-top: 1px solid #f0f0f0;
    box-shadow: 0 -5px 20px rgba(38, 38, 38, 0.035);
    text-align: center;
    .primary {
      min-height: 48px;
      background: linear-gradient(313deg, #ff5612 0%, #fd7539 100%);
      box-shadow: 0 5px 12px rgba(255, 100, 34, 0.16);
      font-weight: 600;
    }
    .muted {
      color: #a3a6ad;
      line-height: 1.6;
      font-size: 10px;
    }
  }
  footer .muted {
    margin-top: 9px;
  }
  // 投保页沿用橙色操作区，协议位于按钮上方，正文预留空间避免被固定底栏覆盖。
  .insure-page {
    padding-bottom: calc(145px + env(safe-area-inset-bottom));
  }
  .insure-page .insurance-footer {
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 390px;
    background: white;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    z-index: 20;
  }
  .insurance-footer .primary {
    background: linear-gradient(313deg, #ff5612, #fd7539);
    min-height: 44px;
  }
  .insurance-result > .primary {
    margin-top: 24px;
  }
  .insurance-detail {
    padding: 9px;
  }
  .large {
    margin: 0 0 12px;
  }
  .important-notice {
    border: 1px solid #aaa;
    border-radius: 5px;
    padding: 7px;
    margin-top: 15px;
    font-size: 12px;
  }
  .important-notice p + p {
    margin-top: 8px;
  }
  .insurance-footer {
    padding: 10px;
    margin-top: 15px;
    border-top: 1px solid #ddd;
  }
  .mask {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1100;
  }
  .authorization-sheet,
  .bank-sheet {
    width: 390px;
    max-width: 100%;
    max-height: 94vh;
    overflow-y: auto;
    background: white;
    border-radius: 12px 12px 0 0;
    padding: 15px 12px 8px;
  }
  .authorization-sheet > h2,
  .bank-sheet > h2 {
    text-align: center;
    margin-bottom: 12px;
  }
  // 授权弹窗独立滚动协议正文，操作区保持在阅读区下方，使用进件页橙色主题。
  .authorization-sheet {
    border-radius: 24px 24px 0 0;
    padding: 22px 20px calc(12px + env(safe-area-inset-bottom));
    max-height: 92vh;
    background: linear-gradient(180deg, #fff8f3 0, #fff 100px);
  }
  .authorization-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 18px;
  }
  .authorization-header h2 {
    margin: 0 0 4px;
    font-size: 19px;
    font-weight: 600;
    color: #30343b;
    line-height: 1.4;
  }
  .authorization-header p {
    font-size: 11px;
    color: #969aa2;
    line-height: 1.5;
  }
  .authorization-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 14px;
    color: #f77337;
    background: #fff0e5;
    font-size: 26px;
  }
  .authorization-intro {
    background: #fff4eb;
    border: 1px solid #ffe7d5;
    border-radius: 12px;
    padding: 12px;
    color: #95684e;
    font-size: 12px;
    line-height: 1.8;
    margin-bottom: 14px;
  }
  .authorization-intro p + p {
    margin-top: 6px;
  }
  .authorization-copy {
    border: 1px solid #eceef1;
    background: #fafbfc;
    border-radius: 12px;
    padding: 14px;
    font-size: 12px;
    color: #626975;
    line-height: 1.85;
    max-height: 35vh;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .authorization-copy h3 {
    color: #383f49;
    font-weight: 600;
    text-align: center;
    margin-bottom: 10px;
    font-size: 14px;
  }
  .authorization-copy .muted {
    margin-top: 12px;
  }
  .authorization-copy ::v-deep img {
    max-width: 100%;
    height: auto;
  }
  .authorization-button {
    border-radius: 24px;
    margin-top: 18px;
    min-height: 46px;
    font-size: 15px;
    font-weight: 600;
    background: linear-gradient(313deg, #ff5612, #fd7539);
    box-shadow: 0 5px 12px rgba(255, 100, 34, 0.15);
  }
  .authorization-button:disabled {
    background: #f3e6dd;
    color: #b69b87;
    box-shadow: none;
    opacity: 1;
  }
  .decline-button {
    display: block;
    width: 100%;
    padding: 13px 0 4px;
    background: transparent;
    color: #979da6;
    font-size: 13px;
  }
  .error {
    color: #c43d2b;
    font-size: 12px;
    padding-top: 8px;
  }
  .bank-sheet {
    position: relative;
    padding: 23px 18px max(18px, env(safe-area-inset-bottom));
    border-radius: 20px 20px 0 0;
  }
  .bank-sheet > h2 {
    font-size: 18px;
    margin-bottom: 18px;
  }
  .close-button {
    position: absolute;
    right: 16px;
    top: 20px;
    width: 28px;
    height: 28px;
    line-height: 26px;
    border-radius: 50%;
    background: #f4f5f7;
    color: #8b919a;
    font-size: 23px;
    font-weight: 300;
  }
  .bank-subtitle {
    margin: 0 0 16px;
    font-size: 12px;
    color: #7a808a;
  }
  // 表单用统一浅底和细分隔线，选择入口与输入框保持相同高度及对齐。
  .bank-fields {
    background: #f7f8fa;
    border: 1px solid #eef0f3;
    border-radius: 12px;
    padding: 0 12px;
    overflow: hidden;
  }
  .bank-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    min-height: 54px;
    border-bottom: 1px solid #e8ebef;
    padding: 14px 0;
    font-size: 13px;
  }
  .bank-row:last-child {
    border: 0;
  }
  .bank-row > span:first-child,
  .bank-row > label {
    flex-shrink: 0;
    color: #5d6570;
  }
  .bank-row input {
    min-width: 0;
    flex: 1;
    width: 0;
    text-align: right;
    font-size: 12px;
    color: #303640;
    background: transparent;
  }
  .bank-row input::placeholder {
    color: #adb3bd;
  }
  .bank-phone-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 28px;
    background: transparent;
    color: #a6adb7;
    font-size: 16px;
  }
  .bank-select {
    width: 100%;
    background: transparent;
    text-align: left;
  }
  .bank-selection {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #303640;
    font-size: 12px;
  }
  .bank-selection.placeholder {
    color: #adb3bd;
  }
  .bank-selection .van-icon {
    color: #a6adb7;
    font-size: 13px;
  }
  .bank-field-error {
    color: #d95035;
    font-size: 11px;
    padding: 0 0 8px;
  }
  .bank-warning {
    background: #fff6ef;
    color: #cf8c64;
    text-align: left;
    font-size: 11px;
    line-height: 1.7;
    padding: 10px 12px;
    border-radius: 8px;
    margin: 16px 0 18px;
  }
  .bank-bottom .primary {
    min-height: 44px;
    margin-top: 12px;
    font-size: 15px;
  }
  .bank-bottom > .muted {
    text-align: center;
    margin-top: 12px;
  }
  .signed-notice-mask {
    z-index: 1300;
    align-items: center;
    padding: 24px;
  }
  .signed-notice-sheet {
    width: 320px;
    max-width: 100%;
    padding: 24px 20px 20px;
    border-radius: 16px;
    background: #fff;
  }
  .signed-notice-sheet h2 {
    text-align: center;
    font-size: 15px;
    line-height: 1.65;
    font-weight: 600;
    color: #30343b;
  }
  .signed-notice-description {
    font-size: 12px;
    line-height: 1.7;
    color: #626975;
    text-align: center;
    margin: 12px 0 18px;
  }
  .signed-notice-sheet .primary {
    min-height: 42px;
    font-size: 14px;
    font-weight: 600;
  }
  .signed-notice-disclaimer {
    margin-top: 18px;
    font-size: 11px;
    line-height: 1.6;
    color: #969aa2;
  }
  .sms-mask {
    z-index: 1300;
    align-items: center;
    padding: 24px;
  }
  .sms-sheet {
    position: relative;
    width: 320px;
    max-width: 100%;
    background: #fff;
    border-radius: 12px;
    padding: 24px 22px;
  }
  .sms-sheet .primary {
    background: linear-gradient(313deg, #ff5612, #fd7539);
  }
  .sms-sheet h2 {
    text-align: center;
    margin-bottom: 22px;
  }
  .sms-sheet .close-button {
    top: 10px;
    right: 10px;
  }
  .sms-input-row {
    display: flex;
    align-items: center;
    background: #f5f6f8;
    border: 1px solid #e4e6ea;
    border-radius: 24px;
    padding: 10px 14px;
    margin-bottom: 24px;
  }
  .sms-input-row input {
    min-width: 0;
    width: 0;
    flex: 1;
    font-size: 14px;
    background: transparent;
  }
  .sms-input-row button {
    background: transparent;
    color: #fd6f34;
    font-size: 12px;
    padding-left: 8px;
  }
  .sms-input-row button:disabled {
    color: #a6acb5;
    cursor: default;
  }
  .protocol-mask {
    align-items: center;
    z-index: 1200;
    padding: 20px;
  }
  .protocol-sheet {
    width: 350px;
    padding: 20px;
    background: white;
    border-radius: 10px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
  }
  .remote-protocol {
    min-height: 0;
    overflow-y: auto;
    margin: 15px 0;
  }
  .local-protocol {
    font-size: 13px;
    line-height: 1.8;
    color: #414751;
    overflow-wrap: anywhere;
    overscroll-behavior: contain;
  }
  .local-protocol ::v-deep p {
    margin: 0 0 12px;
    white-space: pre-wrap;
  }
  .local-protocol ::v-deep strong {
    font-weight: 700;
  }
  .local-protocol ::v-deep .agreement-table {
    overflow-x: auto;
    margin: 12px 0;
  }
  .local-protocol ::v-deep table {
    width: 100%;
    border-collapse: collapse;
  }
  .local-protocol ::v-deep td {
    border: 1px solid #e5e7eb;
    padding: 8px;
    min-width: 90px;
    vertical-align: top;
  }
  .local-protocol ::v-deep td p:last-child {
    margin-bottom: 0;
  }
  .local-protocol ::v-deep a {
    color: #fd6f34;
  }
  .local-protocol ::v-deep .pdf-page {
    margin: 0 0 12px;
  }
  .local-protocol ::v-deep .pdf-page img {
    display: block;
    width: 100%;
    height: auto;
  }
  .local-protocol ::v-deep figcaption {
    text-align: center;
    color: #969aa2;
    font-size: 11px;
    margin-top: 4px;
  }
  .protocol-sheet > .primary {
    flex-shrink: 0;
  }
  .protocol-sheet p {
    margin: 20px 0;
  }
  .loan-result {
    margin: -12px -10px;
    padding: 0 10px 15px;
    background: #f6f6f6;
    min-height: 660px;
  }
  .loan-result > header {
    background: #ff601d;
    color: white;
    text-align: center;
    padding: 24px 5px 60px;
    margin: 0 -10px -40px;
    font-size: 14px;
    font-weight: bold;
  }
  .result-card {
    background: white;
    border-radius: 10px;
    padding: 15px 12px;
    margin: 10px 0;
  }
  .loan-card {
    position: relative;
    text-align: center;
    padding-top: 44px;
  }
  .phone-icon {
    position: absolute;
    top: -23px;
    left: calc(50% - 30px);
    width: 60px;
    height: 60px;
    object-fit: contain;
  }
  .loan-card > .muted {
    margin: 8px 0 18px;
  }
  .loan-info {
    background: #fff6f1;
    padding: 14px 10px;
    border-radius: 6px;
  }
  .loan-info h3 {
    font-weight: bold;
    margin-bottom: 12px;
  }
  .loan-info p {
    font-size: 11px;
    margin: 7px 0;
    text-align: left;
  }
  .loan-info span {
    float: right;
  }
  .loan-result > .primary {
    box-shadow: 0 3px 8px #ffc8a8;
  }
  .loan-progress {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-top: 13px;
    text-align: center;
    font-size: 11px;
  }
  .loan-progress img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  .loan-progress small {
    color: #6ba745;
    font-size: 10px;
  }
  .loan-progress > span {
    color: #ddd;
  }
  .insurance-result {
    border: 1px solid #ddd;
  }
  .insurance-result > p {
    font-size: 11px;
    margin-top: 8px;
  }
  .other-product {
    position: relative;
    padding: 15px;
    margin-top: 12px;
    background: #fff5ef;
    border: 1px solid #ffe0d1;
    border-radius: 7px;
  }
  .other-product h3 {
    font-weight: bold;
    margin-bottom: 14px;
  }
  .other-product strong {
    color: #ff773f;
    font-size: 17px;
  }
  .other-product button {
    position: absolute;
    right: 10px;
    bottom: 18px;
    border-radius: 15px;
    background: #ff773f;
    color: white;
    padding: 5px 12px;
    font-size: 11px;
  }
  .preview-controls {
    max-width: 390px;
    margin: 18px auto;
    padding: 12px;
    color: #777;
    font-size: 11px;
  }
  .preview-controls summary {
    cursor: pointer;
  }
  .preview-controls p {
    margin: 10px 0;
  }
  .preview-controls label {
    display: block;
    margin: 9px 0;
  }
  .preview-controls .primary {
    margin-top: 15px;
    font-size: 13px;
  }
}
</style>
